import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Typography,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  Grid,
  useMediaQuery,
  useTheme,
  Tooltip,
  RadioGroup, Radio,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
  FormControl, FormControlLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Download,
  ForwardToInbox,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  Payment,
  Payments,
  Close
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { get_all_quotations, download_catering_quotation, send_catering_quotation_mail, save_catering_quotation_manual_payment, get_catering_quotation_manual_payments, get_specific_catering_quotation_manual_payment } from '../../services/cateringinfoservice';
import { useNotification } from '../../contexts/NotificationContext';
import CommonModal from '../common/CommonModal';



const QuotationList = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paginationMeta, setPaginationMeta] = useState({
    current_page: 1,
    data_per_page: 10,
    total_count: 0,
    total_pages: 0,
    has_next: false,
    has_previous: false
  });

  const [paymentDetailsOpen, setPaymentDetailsOpen] = useState(false)
  const [manualPaymentOpen, setManualPaymentOpen] = useState(false)
  const [selectedQuotationId, setSelectedQuotationId] = useState(null);
  const [selectedTotalBill, setSelectedTotalBill] = useState(0);
  const [statusChange, setStatusChange] = useState(false)

  const { showSuccess, showError } = useNotification();

  // Use refs to track if this is the first render and to store search timeout
  const isFirstRender = useRef(true);
  const searchTimeout = useRef(null);
  const prevSearchTerm = useRef('');
  const prevPage = useRef(0);
  const prevRowsPerPage = useRef(10);

  // Fetch quotations function
  const fetchQuotations = useCallback(async (searchValue, pageValue, rowsValue) => {
    setLoading(true);
    setError(null);

    try {
      // Convert page from 0-based to 1-based for API
      const apiPage = pageValue + 1;

      const response = await get_all_quotations({
        page: apiPage,
        data_per_page: rowsValue,
        search: searchValue || undefined
      });

      // console.log("Quotation Response => ", response);

      if (response?.data) {
        setQuotations(response.data);
        if (response.pagination) {
          setPaginationMeta(response.pagination);
        }
      } else {
        setQuotations([]);
      }
    }
    catch (error) {
      // console.error("Error Occurred ", error);
      setError("Failed to load quotations. Please try again.");
      showError("Failed to load quotations");
    } finally {
      setLoading(false);
    }
  }, [showError]);

  // Initial load - only once
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      fetchQuotations('', 0, rowsPerPage);
    }
  }, [fetchQuotations, rowsPerPage]);

  useEffect(() => {
    fetchQuotations('', 0, rowsPerPage);
  }, [paymentDetailsOpen, manualPaymentOpen])

  // Handle search with debounce
  useEffect(() => {
    // Clear previous timeout
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    // Don't fetch if search term hasn't changed
    if (searchTerm === prevSearchTerm.current) {
      return;
    }

    // Set new timeout
    searchTimeout.current = setTimeout(() => {
      // Only fetch if search term actually changed
      if (searchTerm !== prevSearchTerm.current) {
        setPage(0); // Reset to first page on search
        fetchQuotations(searchTerm, 0, rowsPerPage);
        prevSearchTerm.current = searchTerm;
      }
    }, 500);

    // Cleanup
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, [searchTerm, rowsPerPage, fetchQuotations]);

  // Handle pagination changes
  useEffect(() => {
    // Skip if it's the first render (already handled by initial load)
    if (isFirstRender.current) {
      return;
    }

    // Check if page or rowsPerPage actually changed
    const pageChanged = page !== prevPage.current;
    const rowsChanged = rowsPerPage !== prevRowsPerPage.current;

    if (pageChanged || rowsChanged) {
      // If rows changed, reset to page 0
      const newPage = rowsChanged ? 0 : page;

      if (rowsChanged) {
        setPage(0);
      }

      fetchQuotations(searchTerm, newPage, rowsPerPage);

      // Update refs
      prevPage.current = newPage;
      prevRowsPerPage.current = rowsPerPage;
    }
  }, [page, rowsPerPage, searchTerm, fetchQuotations]);

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    // Page will be reset to 0 by the useEffect
  };

  // Handle search
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchTerm('');
    // Search effect will handle the fetch
  };

  // Handle refresh
  const handleRefresh = () => {
    fetchQuotations(searchTerm, page, rowsPerPage);
  };

  // Handle actions
  const handleCreateQuotation = () => {
    navigate('/quotations/create-quotation');
  };

  const handleEditQuotation = (id) => {
    navigate('/admin/quotation', {
      state: { quotationId: id }
    });
  };

  const handleDownloadQuotation = async (quotation_id) => {
    try {
      await download_catering_quotation(quotation_id);
      showSuccess("Quotation downloaded successfully");
    }
    catch (error) {
      // console.error(error);
      showError("Failed to download quotation");
    }
  };

  const handleSendQuotationMail = async (id) => {
    try {
      const response = await send_catering_quotation_mail(id);
      showSuccess(response?.message || "Quotation sent successfully");
    }
    catch (error) {
      showError(error?.message || "Failed to send quotation");
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-GB', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  // Mobile Card Component
  const QuotationCard = ({ quotation, index }) => {
    return (
      <Card
        sx={{
          mb: 2,
          borderRadius: 3,
          transition: 'all 0.2s',
          '&:hover': {
            boxShadow: 4,
            transform: 'translateY(-2px)'
          },
          cursor: 'pointer'
        }}
        onClick={() => handleEditQuotation(quotation.id)}
      >
        <CardContent sx={{ p: 2 }}>
          {/* Header Row */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 1.5,
            flexWrap: 'wrap',
            gap: 1
          }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                #{quotation.quotation_number || quotation.name}
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                {quotation.customer_name}
              </Typography>
            </Box>
            <Chip
              label={quotation.payment_status || 'PENDING'}
              sx={{
                bgcolor: quotation.payment_status === "PAID" ? "success.main" : "warning.main",
                color: "white",
                fontWeight: 500,
                fontSize: '0.7rem',
                height: 24
              }}
              size="small"
            />
          </Box>

          {/* Details Grid */}
          <Grid container spacing={1} sx={{ mb: 1.5 }}>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Quotation #
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {quotation.quotation_number || quotation.name}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Event Date
              </Typography>
              <Typography variant="body2">
                {formatDate(quotation.event_date)}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Date Created
              </Typography>
              <Typography variant="body2">
                {formatDate(quotation.created_at)}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              {quotation.customer_phone && (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Phone
                  </Typography>
                  <Typography variant="body2">
                    {quotation.customer_phone}
                  </Typography>
                </>
              )}
            </Grid>
            {quotation.customer_address && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Address
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                  {quotation.customer_address}
                </Typography>
              </Grid>
            )}
          </Grid>

          {/* Action Buttons */}
          <Stack
            direction="row"
            spacing={1}
            justifyContent="flex-end"
            sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}
            onClick={(e) => e.stopPropagation()}
          >
            <Tooltip title="Edit Quotation">
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleEditQuotation(quotation.id)}
                sx={{ bgcolor: 'action.hover' }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Download Quotation">
              <IconButton
                size="small"
                color="info"
                onClick={() => handleDownloadQuotation(quotation.id)}
                sx={{ bgcolor: 'action.hover' }}
              >
                <Download fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Send via Email">
              <IconButton
                size="small"
                color="secondary"
                onClick={() => handleSendQuotationMail(quotation.id)}
                sx={{ bgcolor: 'action.hover' }}
              >
                <ForwardToInbox fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{
      width: '100%',
      p: { xs: 1.5, sm: 2, md: 3 },
      maxWidth: '100%',
      overflowX: 'hidden'
    }}>
      {/* Header Section */}
      {/* <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', sm: 'center' },
        mb: { xs: 2, sm: 3 },
        gap: { xs: 2, sm: 0 }
      }}> */}
      {/* <Typography variant="h5" component="h1" sx={{ 
          fontWeight: 600,
          fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' }
        }}>
          Quotations
        </Typography> */}
      {/* <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, width: { xs: '100%', sm: 'auto' } }}>
          <Button
            variant="outlined"
            onClick={handleRefresh}
            disabled={loading}
            size={isMobile ? "small" : "medium"}
            startIcon={<RefreshIcon />}
            sx={{ 
              flex: { xs: 1, sm: 'none' },
              fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' }
            }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateQuotation}
            sx={{ 
              flex: { xs: 1, sm: 'none' },
              borderRadius: 2,
              fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
              py: { xs: 0.75, sm: 1 }
            }}
          >
            Create New
          </Button>
        </Box> */}
      {/* </Box> */}

      {/* Search Bar */}
      <Box sx={{ mb: { xs: 2, sm: 3 } }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search quotations by customer name or quotation number..."
          value={searchTerm}
          onChange={handleSearchChange}
          size={isMobile ? "small" : "medium"}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
              </InputAdornment>
            ),
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClearSearch}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null
          }}
          sx={{
            '& .MuiInputBase-input': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' }
            }
          }}
        />
      </Box>

      {/* Results Summary */}
      {!loading && !error && quotations.length > 0 && (
        <Box sx={{
          mb: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1
        }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' } }}>
            Showing {page * rowsPerPage + 1} to {Math.min((page + 1) * rowsPerPage, paginationMeta.total_count)} of {paginationMeta.total_count} results
          </Typography>
          {searchTerm && (
            <Chip
              label={`Search: "${searchTerm}"`}
              size="small"
              onDelete={handleClearSearch}
              sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' } }}
            />
          )}
        </Box>
      )}

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={handleRefresh}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Mobile Card View */}
          {isMobile ? (
            <Box sx={{ mb: 2 }}>
              {quotations.length > 0 ? (
                quotations.map((quotation, index) => (
                  <QuotationCard key={quotation.id} quotation={quotation} index={index} />
                ))
              ) : (
                <Paper sx={{ p: { xs: 4, sm: 6 }, textAlign: 'center', borderRadius: 3 }}>
                  <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                    No quotations found
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    {searchTerm
                      ? `No results matching "${searchTerm}"`
                      : "Get started by creating your first quotation"}
                  </Typography>
                  {searchTerm && (
                    <Button
                      variant="outlined"
                      onClick={handleClearSearch}
                      sx={{ mr: 2 }}
                    >
                      Clear Search
                    </Button>
                  )}
                </Paper>
              )}
            </Box>
          ) : (
            // Tablet/Desktop Table View
            <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: 2 }}>
              <TableContainer sx={{ height: 'auto', overflowX: 'auto' }}>
                <Table stickyHeader sx={{ minWidth: 700 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' }, py: { xs: 1, sm: 1.5 } }}>
                        S.No
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' }, py: { xs: 1, sm: 1.5 } }}>
                        Quotation Number
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' }, py: { xs: 1, sm: 1.5 } }}>
                        Customer Name
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' }, py: { xs: 1, sm: 1.5 } }}>
                        Event Date
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' }, py: { xs: 1, sm: 1.5 } }}>
                        Date Created
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' }, py: { xs: 1, sm: 1.5 } }}>
                        Payment Status
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' }, py: { xs: 1, sm: 1.5 } }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {quotations.length > 0 ? (
                      quotations.map((quotation, index) => (
                        <TableRow
                          key={quotation.id}
                          hover
                          // onClick={() => handleEditQuotation(quotation.id)}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: 'action.hover'
                            },
                            transition: 'background-color 0.2s'
                          }}
                        >
                          <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' }, py: { xs: 1, sm: 1.5 } }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 'inherit' }}>
                              {(page * rowsPerPage) + index + 1}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' }, py: { xs: 1, sm: 1.5 } }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.main', fontSize: 'inherit' }}>
                              {quotation.quotation_number || quotation.name}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' }, py: { xs: 1, sm: 1.5 } }}>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 'inherit' }}>
                                {quotation.customer_name}
                              </Typography>
                              {quotation.customer_address && !isTablet && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    display: "block",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    maxWidth: 250,
                                    fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' }
                                  }}
                                >
                                  {quotation.customer_address}
                                </Typography>
                              )}
                              {quotation.customer_phone && (
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' } }}>
                                  {quotation.customer_phone}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' }, py: { xs: 1, sm: 1.5 } }}>
                            <Typography variant="body2" sx={{ fontSize: 'inherit' }}>
                              {quotation.event_date}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' }, py: { xs: 1, sm: 1.5 } }}>
                            <Typography variant="body2" sx={{ fontSize: 'inherit' }}>
                              {quotation.created_at}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' }, py: { xs: 1, sm: 1.5 } }}>
                            <Chip
                              label={quotation.payment_status || 'PENDING'}
                              sx={{
                                bgcolor: quotation.payment_status === "PAID" ? "success.main" : "warning.main",
                                color: "white",
                                fontWeight: 500,
                                minWidth: { xs: 60, sm: 80 },
                                fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' },
                                height: { xs: 22, sm: 24 }
                              }}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="center" onClick={(e) => e.stopPropagation()} sx={{ py: { xs: 1, sm: 1.5 } }}>
                            <Stack direction="row" spacing={{ xs: 0.5, sm: 1 }} justifyContent="center">
                              <Tooltip title="Edit Quotation">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleEditQuotation(quotation.id)}
                                  sx={{
                                    width: { xs: 28, sm: 32 },
                                    height: { xs: 28, sm: 32 }
                                  }}
                                >
                                  <EditIcon fontSize="small" sx={{ fontSize: { xs: 16, sm: 18 } }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Download Quotation">
                                <IconButton
                                  size="small"
                                  color="info"
                                  onClick={() => handleDownloadQuotation(quotation.id)}
                                  sx={{
                                    width: { xs: 28, sm: 32 },
                                    height: { xs: 28, sm: 32 }
                                  }}
                                >
                                  <Download fontSize="small" sx={{ fontSize: { xs: 16, sm: 18 } }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Send via Email">
                                <IconButton
                                  size="small"
                                  color="secondary"
                                  onClick={() => handleSendQuotationMail(quotation.id)}
                                  sx={{
                                    width: { xs: 28, sm: 32 },
                                    height: { xs: 28, sm: 32 }
                                  }}
                                >
                                  <ForwardToInbox fontSize="small" sx={{ fontSize: { xs: 16, sm: 18 } }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Payment Details">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => {
                                    setSelectedQuotationId(quotation.id);
                                    // Optionally fetch total bill from quotation data if available
                                    setSelectedTotalBill(quotation.total_amount || 0);
                                    setPaymentDetailsOpen(true);
                                  }}
                                >
                                  <Payment fontSize="small" />
                                </IconButton>
                              </Tooltip>

                              <Tooltip title="Manual Payment">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => {
                                    setSelectedQuotationId(quotation.id);
                                    setSelectedTotalBill(quotation.total_amount || 0);
                                    setManualPaymentOpen(true);
                                  }}
                                >
                                  <Payments fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: { xs: 6, sm: 8 } }}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                              No quotations found
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                              {searchTerm
                                ? `No results matching "${searchTerm}"`
                                : "Get started by creating your first quotation"}
                            </Typography>
                            {searchTerm && (
                              <Button
                                variant="outlined"
                                onClick={handleClearSearch}
                                sx={{ mr: 2 }}
                              >
                                Clear Search
                              </Button>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              {paginationMeta.total_count > 0 && (
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={paginationMeta.total_count}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  showFirstButton={!isMobile}
                  showLastButton={!isMobile}
                  sx={{
                    '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                      fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' }
                    },
                    '.MuiSelect-select': {
                      fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' }
                    },
                    '.MuiTablePagination-actions button': {
                      width: { xs: 28, sm: 32 },
                      height: { xs: 28, sm: 32 }
                    }
                  }}
                />
              )}
            </Paper>
          )}

          {/* Mobile Pagination */}
          {isMobile && paginationMeta.total_count > rowsPerPage && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <TablePagination
                rowsPerPageOptions={[]}
                component="div"
                count={paginationMeta.total_count}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                showFirstButton
                showLastButton
                sx={{
                  '.MuiTablePagination-displayedRows': {
                    fontSize: '0.7rem'
                  },
                  '.MuiTablePagination-actions button': {
                    width: 28,
                    height: 28
                  }
                }}
              />
            </Box>
          )}

          <CommonModal modalOpen={paymentDetailsOpen} setModalOpen={setPaymentDetailsOpen} children={
            <PaymentDetailModal
              open={paymentDetailsOpen}
              onClose={() => setPaymentDetailsOpen(false)}
              quotationId={selectedQuotationId}
            />
          } modalWidth={200} />

          <CommonModal modalOpen={manualPaymentOpen} setModalOpen={setManualPaymentOpen} children={
            <ManualPaymentModal
              open={manualPaymentOpen}
              onClose={() => setManualPaymentOpen(false)}
              quotationId={selectedQuotationId}
              totalBill={selectedTotalBill}
              modalWidth={200}
            />
          } />

        </>
      )}
    </Box>
  );
};




// ==================== PaymentDetailModal ====================
const PaymentDetailModal = ({ open, onClose, quotationId }) => {
  const [paymentList, setPaymentList] = useState([]);
  const [paymentStatus, setPaymentStatus] = useState(false);

  const getPaymentLists = async () => {
    try {
      const response = await get_catering_quotation_manual_payments(quotationId);
      // console.log(response);
      setPaymentList(response.data);
      setPaymentStatus(response.status);
    } catch (error) {
      console.error("Something Went Wrong");
    }
  };

  useEffect(() => {
    if (open) {
      getPaymentLists();
    }
  }, [open]);

  // Calculate last pending amount
  const getLastPendingAmount = () => {
    if (paymentList.length > 0) {
      return paymentList[paymentList.length - 1].pending_amount;
    }
    return 0;
  };

  const lastPendingAmount = getLastPendingAmount();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md" // Increased width
      fullWidth // Makes it take full width up to maxWidth
      PaperProps={{
        sx: {
          bgcolor: '#fafafa',
          width: '700px', // Custom width
          maxWidth: '90vw', // Responsive on smaller screens
        }
      }}
    >
      <DialogTitle sx={{
        bgcolor: 'primary.main',
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Typography variant="h6" fontWeight={700}>
          Payment Details
        </Typography>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
        {paymentList.length === 0 ? (
          // Show pending status when paymentStatus is false
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="warning.main" gutterBottom>
              ⚠️ Payment Pending
            </Typography>
            {/* <Typography variant="body1" color="text.secondary">
              Total Pending Amount:
              <Typography variant="h5" color="error.main" component="span" sx={{ ml: 1, fontWeight: 700 }}>
                S$ {lastPendingAmount.toFixed(2)}
              </Typography>
            </Typography> */}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              No payments have been recorded for this quotation yet.
            </Typography>
          </Box>
        ) : (
          // Show payment list when status is true
          <>
            {/* Show final pending amount if any */}
            {/* Show final pending amount if any */}
            {lastPendingAmount > 0 && (
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  bgcolor: '#fff3e0',
                  borderRadius: 1,
                  border: '1px solid #ffb74d',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap'
                }}
              >
                <Typography variant="subtitle1" fontWeight={600} color="text.secondary">
                  Remaining Pending Amount:
                </Typography>
                <Typography variant="h4" color="error.main" fontWeight={700}>
                  S$ {lastPendingAmount.toFixed(2)}
                </Typography>
              </Box>
            )}
            <List>
              {paymentList.map((p, idx) => (
                <Box key={idx}>
                  <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                    <ListItemText
                      primary={
                        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap">
                          <Typography variant="subtitle1" fontWeight={600}>
                            Payment #{idx + 1}
                          </Typography>
                          <Chip
                            label={p.pending_amount === 0 ? 'Completed' : 'Partial'}
                            size="small"
                            sx={{
                              bgcolor: p.pending_amount === 0 ? 'success.main' : 'warning.main',
                              color: 'white',
                              fontWeight: 500,
                            }}
                          />
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2" color="text.secondary" component="span">
                            Paid Date: {p.paid_date}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="primary"
                            component="span"
                            sx={{
                              ml: { xs: 0, sm: 2 },
                              display: { xs: 'block', sm: 'inline-block' },
                              fontWeight: 600,
                              color: 'primary.main'
                            }}
                          >
                            Amount Paid : S$ {p.amount_paid.toFixed(2)}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            component="span"
                            sx={{
                              ml: { xs: 0, sm: 2 },
                              display: { xs: 'block', sm: 'inline-block' }
                            }}
                          >
                            Pending: S$ {p.pending_amount.toFixed(2)}
                          </Typography>
                          {p.payment_method && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              component="span"
                              sx={{
                                ml: { xs: 0, sm: 2 },
                                display: { xs: 'block', sm: 'inline-block' }
                              }}
                            >
                              Method: {p.payment_method || 'N/A'}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                  {idx < paymentList.length - 1 && <Divider />}
                </Box>
              ))}
            </List>



            {paymentList.length === 0 && (
              <Typography variant="body1" color="text.secondary" align="center" sx={{ mt: 4 }}>
                No payments recorded for this quotation.
              </Typography>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: '#f5f5f5' }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            bgcolor: 'primary.main',
            '&:hover': { bgcolor: 'primary.dark' },
            px: 4,
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};




// ==================== ManualPaymentModal ====================
const ManualPaymentModal = ({ open, onClose, quotationId, totalBill = 0 }) => {
  // Local state
  const [selectedPayment, setSelectedPayment] = useState('');
  const [amountReceived, setAmountReceived] = useState('');
  const [reference, setReference] = useState('');
  const [totalAmount, setTotalAmount] = useState(0)

  // Calculate balance (amount received - total bill)
  const receivedNum = parseFloat(amountReceived) || 0;
  const balance = receivedNum - totalAmount;

  const getSpecificPaymentDetail = async () => {
    try {
      const response = await get_specific_catering_quotation_manual_payment(quotationId);
      setTotalAmount(response.pending_amount)
      // console.log(response)
    }
    catch (error) {
      console.error("Error While Getting Response")
    }
  }
  useEffect(() => {
    getSpecificPaymentDetail()
  }, [])

  const handleCloseBill = async () => {
    if (!selectedPayment) {
      // show error – use notification context if available
      return;
    }
    if (selectedPayment === 'Cash' && (receivedNum < totalBill)) {
      // show error
      return;
    }
    // Here you would call your API to record the payment
    // console.log({ quotationId, selectedPayment, amountReceived: receivedNum, balance, reference });

    let payload = {
      "catering_quotation_id": parseInt(quotationId),
      "amount_paid": amountReceived,
      "payment_method": selectedPayment
    }

    try {
      const response = await save_catering_quotation_manual_payment(payload);

    }
    catch (error) {

    }

    onClose();
    // Reset state after close
    setSelectedPayment('');
    setAmountReceived('');
    setReference('');
  };

  const handleCancel = () => {
    onClose();
    setSelectedPayment('');
    setAmountReceived('');
    setReference('');
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#fafafa',
          width: '700px', // Fixed width
          maxWidth: '90vw', // Responsive
        }
      }}
    >
      <DialogTitle sx={{
        bgcolor: 'primary.main',
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Typography variant="h6" fontWeight={700}>
          Manual Payment
        </Typography>
        <IconButton onClick={handleCancel} sx={{ color: 'white' }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: { xs: 2, sm: 4 } }}>
        {totalAmount > 0 ?

          <Box sx={{ maxWidth: '100%', mx: 'auto' }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ color: 'primary.main' }}>
              Select Payment Method
            </Typography>
            <RadioGroup
              sx={{ ml: 1 }}
              value={selectedPayment}
              onChange={(e) => {
                setSelectedPayment(e.target.value);
                setAmountReceived('');
              }}
            >
              <FormControlLabel
                value="PayNow"
                control={<Radio sx={{ color: 'primary.main', '&.Mui-checked': { color: 'primary.main' } }} />}
                label="PayNow"
              />
              <FormControlLabel
                value="Nets"
                control={<Radio sx={{ color: 'primary.main', '&.Mui-checked': { color: 'primary.main' } }} />}
                label="Nets"
              />
              <FormControlLabel
                value="Visa/Mastercard"
                control={<Radio sx={{ color: 'primary.main', '&.Mui-checked': { color: 'primary.main' } }} />}
                label="Visa / MasterCard"
              />
              <FormControlLabel
                value="Cash"
                control={<Radio sx={{ color: 'primary.main', '&.Mui-checked': { color: 'primary.main' } }} />}
                label="Cash"
              />
            </RadioGroup>

            <Box sx={{ mt: 4, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Total Bill Amount
              </Typography>
              <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 700 }}>
                S$ {totalAmount.toFixed(2)}
              </Typography>
            </Box>

            <TextField
              label="Amount Received"
              fullWidth
              value={amountReceived}
              onChange={(e) => {
                const val = e.target.value;
                // Allow only numbers and up to 2 decimals
                if (/^\d*\.?\d{0,2}$/.test(val) || val === '') {
                  setAmountReceived(val);
                }
              }}
              placeholder="Enter amount received"
              sx={{ mt: 2 }}
              InputProps={{
                startAdornment: <InputAdornment position="start">S$</InputAdornment>,
              }}
              error={amountReceived !== '' && isNaN(parseFloat(amountReceived))}
              helperText={amountReceived !== '' && isNaN(parseFloat(amountReceived)) ? 'Please enter a valid number' : ''}
              disabled={!selectedPayment} // Disable if no payment method selected
            />

            {selectedPayment === 'Cash' && (
              <Box sx={{
                mt: 2,
                p: 2,
                bgcolor: amountReceived !== '' && receivedNum < totalBill ? '#ffebee' : '#e8f5e9',
                borderRadius: 2,
                width: '100%'
              }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Cash Payment Selected
                </Typography>
                {amountReceived !== '' && receivedNum < totalBill && (
                  <Typography sx={{ color: '#d32f2f', mt: 1, fontSize: '0.875rem' }}>
                    ⚠️ Amount received is less than the bill amount. Please enter the full amount.
                  </Typography>
                )}
                {amountReceived !== '' && receivedNum >= totalBill && (
                  <Typography sx={{ color: '#2e7d32', mt: 1, fontSize: '0.875rem' }}>
                    ✅ Amount received is sufficient. You can proceed with closing the bill.
                  </Typography>
                )}
                {amountReceived === '' && (
                  <Typography sx={{ color: '#666', mt: 1, fontSize: '0.875rem' }}>
                    Please enter the amount received in cash.
                  </Typography>
                )}
              </Box>
            )}
          </Box> : <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body1" sx={{ color: 'primary.main', fontWeight: 600 }}>
              ✅ This bill has been fully paid
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
              No further payment is required for this quotation.
            </Typography>
          </Box>}
      </DialogContent>
      {totalAmount > 0 &&
        <DialogActions sx={{ p: 2, bgcolor: '#f5f5f5', justifyContent: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={handleCancel}
            sx={{
              borderColor: 'primary.main',
              color: 'primary.main',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.light', opacity: 0.1 },
              minWidth: 120,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCloseBill}
            disabled={
              !selectedPayment ||
              (selectedPayment === 'Cash' && (amountReceived === '' || receivedNum < totalBill))
            }
            sx={{
              bgcolor: 'primary.main',
              '&:hover': { bgcolor: 'primary.dark' },
              '&.Mui-disabled': { bgcolor: '#cccccc' },
              minWidth: 120,
            }}
          >
            Submit
          </Button>
        </DialogActions>}
    </Dialog>
  );
};


export default QuotationList;